import {
  DeesElement,
  html,
  customElement,
  type TemplateResult,
  property,
  state,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { viewerStyles } from './styles.js';
import { mediaControls } from '../viewer.styles.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { demo } from './demo.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-image-viewer': DeesImageViewer;
  }
}

@customElement('dees-image-viewer')
export class DeesImageViewer extends DeesElement {
  public static styles = [themeDefaultStyles, viewerStyles, mediaControls];
  public static demo = demo;
  public static demoGroups = ['Media'];

  @property()
  accessor src: string = '';

  @property()
  accessor filename = '';

  @property()
  accessor alt: string = '';

  @property()
  accessor fit: 'contain' | 'cover' | 'actual' = 'contain';

  @property({ type: Boolean })
  accessor showToolbar: boolean = true;

  @state()
  accessor zoom: number = 1;

  @state()
  accessor panX: number = 0;

  @state()
  accessor panY: number = 0;

  @state()
  accessor isDragging: boolean = false;

  @state()
  accessor loading: boolean = true;

  @state()
  accessor error: string = '';

  @state()
  accessor imageNaturalWidth: number = 0;

  @state()
  accessor imageNaturalHeight: number = 0;

  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartPanX = 0;
  private dragStartPanY = 0;

  public render(): TemplateResult {
    return html`
      <div class="viewer-container">
        ${this.showToolbar ? html`
          <div class="toolbar">
            <div class="toolbar-group">
              <button type="button" class="toolbar-button" @click=${this.zoomOut} title="Zoom out" aria-label="Zoom out" ?disabled=${this.zoom <= 0.1}>
                <dees-icon icon="lucide:ZoomOut"></dees-icon>
              </button>
              <button type="button" class="toolbar-button" @click=${this.resetZoom} aria-label="Reset zoom" title="Reset zoom">
                <span class="zoom-level">${Math.round(this.zoom * 100)}%</span>
              </button>
              <button type="button" class="toolbar-button" @click=${this.zoomIn} title="Zoom in" aria-label="Zoom in" ?disabled=${this.zoom >= 10}>
                <dees-icon icon="lucide:ZoomIn"></dees-icon>
              </button>
            </div>
            <div class="toolbar-group">
              <dees-input-multitoggle label="Image size" labelPosition="none" size="sm"
                .options=${[{ label: 'Fit', key: 'contain' }, { label: 'Actual', key: 'actual' }]}
                .value=${this.fit === 'actual' ? 'actual' : 'contain'}
                @change=${(event: CustomEvent) => event.detail.value === 'actual' ? this.actualSize() : this.fitToScreen()}></dees-input-multitoggle>
            </div>
            <div class="toolbar-group">
              <button type="button" class="toolbar-button" @click=${this.download} title="Download">
                <dees-icon icon="lucide:Download"></dees-icon>
              </button>
            </div>
            ${this.imageNaturalWidth > 0 ? html`
              <div class="toolbar-group toolbar-group--end">
                <span class="image-info">${this.imageNaturalWidth} x ${this.imageNaturalHeight}</span>
              </div>
            ` : ''}
          </div>
        ` : ''}

        <div
          class="image-area" style=${`--image-cursor: ${this.zoom > 1 || this.fit === 'actual' ? (this.isDragging ? 'grabbing' : 'grab') : 'default'}`}
          @wheel=${this.handleWheel}
          @pointerdown=${this.handleMouseDown}
          @pointermove=${this.handleMouseMove}
          @pointerup=${this.handleMouseUp}
          @pointercancel=${this.handleMouseUp}
          @dblclick=${this.handleDoubleClick}
        >
          <div class="checkerboard"></div>
          <div class="image-wrapper ${this.isDragging ? 'dragging' : ''}" style=${`--pan-x:${this.panX}px;--pan-y:${this.panY}px;--zoom:${this.zoom}`}>
            ${this.src ? html`
              <img
                class="${this.fit === 'actual' ? 'actual' : ''}"
                style=${`--image-fit:${this.fit}`}
                src="${this.src}"
                alt="${this.alt}"
                @load=${this.handleImageLoad}
                @error=${this.handleImageError}
                draggable="false"
              />
            ` : ''}
          </div>
          ${this.loading && this.src ? html`
            <div class="loading-overlay">
              <div class="loading-spinner"></div>
            </div>
          ` : ''}
          ${this.error ? html`
            <div class="error-overlay">
              <dees-icon class="error-icon" icon="lucide:ImageOff"></dees-icon>
              <span class="error-text">${this.error}</span>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  public zoomIn(): void {
    this.zoom = Math.min(10, this.zoom * 1.25);
  }

  public zoomOut(): void {
    this.zoom = Math.max(0.1, this.zoom / 1.25);
    if (this.zoom <= 1) {
      this.panX = 0;
      this.panY = 0;
    }
  }

  public resetZoom(): void {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
  }

  public fitToScreen(): void {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.fit = 'contain';
  }

  public actualSize(): void {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.fit = 'actual';
  }

  public download(): void {
    if (!this.src) return;
    const link = document.createElement('a');
    link.href = this.src;
    link.download = this.filename || (this.src.startsWith('blob:') || this.src.startsWith('data:') ? 'image' : this.src.split('/').pop()?.split(/[?#]/)[0] || 'image');
    link.click();
  }

  private handleImageLoad(e: Event): void {
    const img = e.target as HTMLImageElement;
    this.loading = false;
    this.error = '';
    this.imageNaturalWidth = img.naturalWidth;
    this.imageNaturalHeight = img.naturalHeight;
  }

  private handleImageError(): void {
    this.loading = false;
    this.error = 'Failed to load image';
  }

  private handleWheel(e: WheelEvent): void {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(10, Math.max(0.1, this.zoom * delta));
    this.zoom = newZoom;
    if (this.zoom <= 1) {
      this.panX = 0;
      this.panY = 0;
    }
  }

  private handleMouseDown(e: PointerEvent): void {
    if (e.button !== 0 || (this.zoom <= 1 && this.fit !== 'actual')) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.dragStartPanX = this.panX;
    this.dragStartPanY = this.panY;
  }

  private handleMouseMove(e: PointerEvent): void {
    if (!this.isDragging) return;
    this.panX = this.dragStartPanX + (e.clientX - this.dragStartX);
    this.panY = this.dragStartPanY + (e.clientY - this.dragStartY);
  }

  private handleMouseUp(): void {
    this.isDragging = false;
  }

  private handleDoubleClick(): void {
    if (this.zoom === 1) {
      this.zoom = 2;
    } else {
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
    }
  }

  public updated(changedProperties: Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);
  }

  public willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    super.willUpdate(changedProperties);
    if (changedProperties.has('src')) {
      this.loading = !!this.src;
      this.error = '';
      this.zoom = 1;
      this.panX = 0;
      this.panY = 0;
      this.imageNaturalWidth = 0;
      this.imageNaturalHeight = 0;
    }
  }
}
